import { FFmpeg } from '@ffmpeg/ffmpeg';
import { fetchFile, toBlobURL } from '@ffmpeg/util';

type ConvertProgressCallback = (progress: number) => void;

const FFMPEG_CORE_VERSION = '0.12.9';
const FFMPEG_CORE_BASE_URL = `https://unpkg.com/@ffmpeg/core@${FFMPEG_CORE_VERSION}/dist/umd`;

const ffmpeg = new FFmpeg();
let ffmpegLoadPromise: Promise<boolean> | null = null;

const getBaseFilename = (filename: string) => {
  const normalized = filename.trim() || 'spot-video';
  return normalized.replace(/\.[^/.]+$/, '') || 'spot-video';
};

const getExtension = (filename: string) => {
  const match = filename.trim().match(/\.([^.]+)$/);
  return match?.[1]?.toLowerCase() || '';
};

export const parseAndValidateVideoFile = (file: File) => {
  const extension = getExtension(file.name);
  const isWebm = file.type === 'video/webm' || extension === 'webm';
  const needsConversion =
    file.type === 'video/mp4' ||
    file.type === 'video/quicktime' ||
    ['mp4', 'mov'].includes(extension);

  if (!isWebm && !needsConversion) {
    return {
      ok: false as const,
      message: 'Please upload a WebM video, or an MP4/MOV file to convert.',
    };
  }

  return {
    ok: true as const,
    isWebm,
    needsConversion,
  };
};

const loadFfmpeg = async () => {
  if (ffmpeg.loaded) {
    return true;
  }
  if (!ffmpegLoadPromise) {
    ffmpegLoadPromise = ffmpeg.load({
      coreURL: await toBlobURL(
        `${FFMPEG_CORE_BASE_URL}/ffmpeg-core.js`,
        'text/javascript',
      ),
      wasmURL: await toBlobURL(
        `${FFMPEG_CORE_BASE_URL}/ffmpeg-core.wasm`,
        'application/wasm',
      ),
    });
  }
  return ffmpegLoadPromise;
};

export const convertVideoFileToWebm = async (
  file: File,
  onProgress?: ConvertProgressCallback,
) => {
  const validation = parseAndValidateVideoFile(file);
  if (!validation.ok) {
    throw new Error(validation.message);
  }

  const outputFilename = `${getBaseFilename(file.name)}.webm`;

  if (validation.isWebm && !validation.needsConversion) {
    return new File([file], outputFilename, { type: 'video/webm' });
  }

  await loadFfmpeg();

  const inputExtension = getExtension(file.name) || 'mp4';
  const inputFilename = `input-${Date.now()}.${inputExtension}`;
  const outputPath = `output-${Date.now()}.webm`;

  const handleProgress = ({ progress }: { progress: number }) => {
    if (onProgress) {
      onProgress(Math.round(Math.min(Math.max(progress, 0), 1) * 100));
    }
  };

  ffmpeg.on('progress', handleProgress);

  try {
    await ffmpeg.writeFile(inputFilename, await fetchFile(file));

    const exitCode = await ffmpeg.exec([
      '-i',
      inputFilename,
      '-c:v',
      'libvpx',
      '-b:v',
      '1M',
      '-c:a',
      'libopus',
      '-f',
      'webm',
      outputPath,
    ]);

    if (exitCode !== 0) {
      throw new Error(`FFmpeg exited with code ${exitCode}.`);
    }

    const outputData = await ffmpeg.readFile(outputPath);
    const bytes =
      typeof outputData === 'string'
        ? new TextEncoder().encode(outputData)
        : outputData;

    return new File([bytes], outputFilename, { type: 'video/webm' });
  } finally {
    ffmpeg.off('progress', handleProgress);
    await ffmpeg.deleteFile(inputFilename).catch(() => undefined);
    await ffmpeg.deleteFile(outputPath).catch(() => undefined);
  }
};
